/**
 * @created     2026-03-27
 * @description SEPA-Lastschrift Mandat Modal.
 *              Bankdaten (IBAN, Kontoinhaber) werden NIEMALS im Chat eingegeben.
 *              FEAT-022: SEPA-Mandat Formular mit IBAN-Validierung (mod97).
 */
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    ChangeDetectionStrategy,
    SimpleChanges,
    ViewChild,
  signal,
  computed,
  isDevMode,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../pipes/translate.pipe';
import {
  isValidContractDebitDay,
  isValidIban,
  normalizeIban,
} from '../../utils/form-validators';

export interface SepaMandat {
  iban: string;
  kontoinhaber: string;
  abbuchungstag: string;
}

@Component({
  selector: 'app-sepa-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './sepa-modal.component.html',
  styleUrl: './sepa-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SepaModalComponent implements OnChanges, AfterViewInit {
  @ViewChild('dialog', { static: true }) private dialog!: ElementRef<HTMLDialogElement>;
    @Input() defaultDebitDay = '';
    @Output() close = new EventEmitter<void>();
  @Output() submitMandat = new EventEmitter<SepaMandat>();

  private readonly isDevEnvironment = isDevMode();
  readonly showDevHelper = this.isDevEnvironment;
  readonly devTestIban = 'DE89 3704 0044 0532 0130 00';

  readonly ibanRaw    = signal('');
  readonly accountHolder = input('');
  readonly abbuchungstag = signal('');
  readonly mandatAccepted = signal(false);
  readonly submitAttempted = signal(false);

  /** Formatted IBAN for display (groups of 4) */
  readonly ibanFormatted = computed(() => this.formatIban(this.ibanRaw()));

  readonly ibanValid = computed(() => isValidIban(this.ibanRaw()));
  readonly debitDayValid = computed(() => isValidContractDebitDay(this.abbuchungstag()));

    readonly isValid = computed(() => {
    return (
      this.ibanValid() &&
      this.accountHolder().trim().length >= 2 &&
      this.debitDayValid() &&
      this.mandatAccepted()
    );
    });

    ngOnChanges(changes: SimpleChanges): void {
      if (!changes['defaultDebitDay'] || this.abbuchungstag()) {
        return;
      }

      if (this.defaultDebitDay === '1' || this.defaultDebitDay === '15') {
        this.abbuchungstag.set(this.defaultDebitDay);
      }
    }

  private formatIban(raw: string): string {
    const clean = raw.replace(/\s/g, '').toUpperCase().slice(0, 34);
    return clean.match(/.{1,4}/g)?.join(' ') ?? clean;
  }

  onIbanInput(event: Event): void {
    const raw = normalizeIban((event.target as HTMLInputElement).value).slice(0, 34);
    this.ibanRaw.set(raw);
    // Update input display
    const input = event.target as HTMLInputElement;
    const formatted = this.formatIban(raw);
    input.value = formatted;
  }

  ngAfterViewInit(): void {
    this.dialog.nativeElement.showModal();
  }

  onClose(): void {
    this.dialog.nativeElement.close();
    this.close.emit();
  }

  insertTestData(): void {
    if (!this.showDevHelper) return;
    this.ibanRaw.set(normalizeIban(this.devTestIban));
    this.abbuchungstag.set('15');
    this.mandatAccepted.set(true);
  }

  onSubmit(): void {
    this.submitAttempted.set(true);
    if (!this.isValid()) return;
    this.submitMandat.emit({
      iban: this.ibanRaw(),
      kontoinhaber: this.accountHolder().trim(),
      abbuchungstag: this.abbuchungstag(),
    });
  }
}
